const DOMAIN_RULES = [
  { name: 'scuola', hints: ['scuola', 'classe', 'compito', 'interrogazione', 'medie', 'liceo'] },
  { name: 'università', hints: ['università', 'esame', 'corso', 'tesi', 'cfu', 'professore', 'appello'] },
  { name: 'professionale', hints: ['cliente', 'azienda', 'kpi', 'deadline', 'progetto', 'roadmap', 'budget', 'team'] },
  { name: 'brainstorming', hints: ['idea', 'brainstorming', 'mappa mentale', 'ipotesi', 'alternativa', 'opzione'] },
];

const inferDomainContext = (node, neighbors = [], currentSummary = '') => {
  const source = [
    node?.title || '',
    node?.summary || '',
    node?.description || '',
    currentSummary || '',
    ...neighbors.flatMap(n => [n?.title || '', n?.summary || '', n?.description || '']),
  ].join(' ').toLowerCase();

  const scored = DOMAIN_RULES.map(rule => ({
    name: rule.name,
    score: rule.hints.reduce((acc, h) => (source.includes(h) ? acc + 1 : acc), 0),
  }));

  const best = scored.sort((a, b) => b.score - a.score)[0];
  return best && best.score > 0 ? best.name : 'generale';
};

const getDomainFocus = (domain) => {
  if (domain === 'scuola') return 'spiegazioni semplici, lessico concreto, supporto a ripasso rapido';
  if (domain === 'università') return 'precisione concettuale, relazioni causa-effetto, lessico disciplinare';
  if (domain === 'professionale') return 'orientamento operativo, impatto pratico, decisioni e trade-off';
  if (domain === 'brainstorming') return 'divergenza+convergenza, alternative, sintesi azionabile';
  return 'chiarezza, contesto e utilità immediata';
};

const buildGraphContext = (neighbors = []) => {
  if (!neighbors.length) return 'Nessun nodo collegato.';
  return neighbors
    .slice(0, 6)
    .map((n, i) => {
      const role = n?.type === 'problem' ? 'tema/problema' : 'idea/soluzione';
      const summary = (n?.summary || '').trim();
      return `${i + 1}) [${role}] ${n?.title || 'Nodo senza titolo'}${summary ? ` — ${summary}` : ''}`;
    })
    .join('\n');
};

const FLASHCARD_BODY = `You are an expert tutor in active learning on concept maps.
Generate ONE structured flashcard targeted to the graph context.

OUTPUT FORMAT:
Return ONLY valid JSON with these keys:
{
  "type": "cloze" | "definition" | "compare" | "cause" | "example",
  "front": "question or cloze",
  "back": "concise answer",
  "summary": "brief node summary"
}

RULES:
1. Prefer "cloze" when the node lends itself to precise recall.
2. If using "cloze", include at least one pair of curly braces { } in the "front" field.
3. "front" brief: 8-18 words.
4. "back" concise: 8-24 words.
5. "summary" useful as node text: 12-28 words.
6. No extra keys, no markdown, no text outside the JSON.`;

const LANG_FALLBACKS = { en: 'English', it: 'Italian' };

export const getFlashcardSystemPrompt = (lang = 'en') => {
  const fallback = LANG_FALLBACKS[lang] ?? 'English';
  return `${FLASHCARD_BODY}\n\nLANGUAGE: respond in the same language as the node content. If you cannot determine the language, use ${fallback} as fallback.`;
};

export const FLASHCARD_SYSTEM_PROMPT = getFlashcardSystemPrompt('it');

export const CONTENT_SYSTEM_PROMPT = `Sei un assistente di studio e ragionamento su mappe concettuali.
Generi una summary centrata sul dominio d'uso del grafo.

REGOLE:
1. Integra nodo target + contesto dei nodi collegati.
2. Adatta tono e focus al dominio inferito (scuola, università, professionale, brainstorming, generale).
3. Output in un unico paragrafo di 25-55 parole.
4. Inserisci una micro-utilità pratica (es. cosa chiarisce o come usarlo).
5. Niente parentesi graffe { }.
6. Solo testo finale, nessuna etichetta.

LINGUA: rispondi sempre nella stessa lingua con cui scrive l'utente. Se non riesci a determinare la lingua, usa l'italiano come fallback.`;

export const constructFlashcardUserPrompt = (node, neighbors, currentSummary, rootNode) => {
  const domain = inferDomainContext(node, neighbors, currentSummary);
  const graphContext = buildGraphContext(neighbors);
  const domainFocus = getDomainFocus(domain);
  const hasSummary = Boolean(currentSummary && currentSummary.trim());
  const mapTopic = rootNode && rootNode.id !== node.id
    ? `\nARGOMENTO PRINCIPALE DELLA MAPPA: "${rootNode.title}"${rootNode.summary ? ` — ${rootNode.summary}` : ''}`
    : '';

  return `
DOMINIO INFERITO: ${domain}
FOCUS DOMINIO: ${domainFocus}
${mapTopic}
NODO TARGET:
- tipo: ${node?.type || 'unknown'}
- titolo: "${node?.title || ''}"
- summary esistente: "${currentSummary || node?.summary || ''}"

CONTESTO GRAFO (nodi collegati):
${graphContext}

${hasSummary
  ? `ISTRUZIONE: La summary è già scritta. Trasformala in cloze aggiungendo { } intorno a 1-3 termini chiave da ricordare. Usa il testo così com'è nel campo "front" (non tagliare, non riscrivere). In "back" spiega brevemente quei termini nel contesto dell'argomento principale. In "summary" ripeti il "front".`
  : `ISTRUZIONE: Crea la frase cloze migliore per testare la comprensione del nodo. Tutti i termini tecnici devono essere interpretati nel contesto dell'argomento principale della mappa.`
}
`;
};

const extractJsonBlock = (text = '') => {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced?.[1]) return fenced[1].trim();
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start >= 0 && end > start) return text.slice(start, end + 1);
  return text.trim();
};

export const parseFlashcardResponse = (response, fallback = {}) => {
  const raw = extractJsonBlock(response);
  try {
    const parsed = JSON.parse(raw);
    return {
      type: parsed.type || 'cloze',
      front: (parsed.front || fallback.summary || fallback.title || '').trim(),
      back: (parsed.back || fallback.description || fallback.title || '').trim(),
      summary: (parsed.summary || fallback.summary || '').trim(),
    };
  } catch {
    const front = raw.trim();
    return {
      type: 'cloze',
      front,
      back: (fallback.description || fallback.title || '').trim(),
      summary: (fallback.summary || front).trim(),
    };
  }
};

export const constructContentUserPrompt = (node, neighbors, rootNode) => {
  const domain = inferDomainContext(node, neighbors, node?.summary || '');
  const graphContext = buildGraphContext(neighbors);
  const domainFocus = getDomainFocus(domain);
  const mapTopic = rootNode && rootNode.id !== node.id
    ? `\nARGOMENTO PRINCIPALE DELLA MAPPA: "${rootNode.title}"${rootNode.summary ? ` — ${rootNode.summary}` : ''}`
    : '';

  return `
DOMINIO INFERITO: ${domain}
FOCUS DOMINIO: ${domainFocus}
${mapTopic}
NODO TARGET:
- tipo: ${node?.type || 'unknown'}
- titolo: "${node?.title || ''}"
- summary attuale: "${node?.summary || ''}"
- note: "${node?.description || ''}"

CONTESTO GRAFO (nodi collegati):
${graphContext}

Genera una summary migliorata, più centrata sul dominio, utile per studio/brainstorming/decisione. Tutti i termini tecnici devono essere interpretati nel contesto dell'argomento principale della mappa.
`;
};
